import React from 'react'
import { cn, Slider } from '@amusnet/react-ui'
import { BetStepperButton } from '@components'

// Same components everywhere; isPortrait/isLandscape only change CSS sizes.
type UltraPlaySliderRowProps = {
    isLandscape?: boolean
    isPortrait?: boolean
    label: string
    amount: string
    currency?: string
    currentIndex: number
    maxIndex: number
    onDecrement: () => void
    onIncrement: () => void
    onIndexChange: (index: number) => void
    // Fired when the player releases the slider (commits the value) — used to sync the engine once,
    // instead of on every drag tick.
    onCommit?: (index: number) => void
    canDecrement: boolean
    canIncrement: boolean
    disabled?: boolean
}

const UltraPlaySliderRow: React.FC<UltraPlaySliderRowProps> = ({
    isLandscape,
    isPortrait,
    label,
    amount,
    currency,
    currentIndex,
    maxIndex,
    onDecrement,
    onIncrement,
    onIndexChange,
    onCommit,
    canDecrement,
    canIncrement,
    disabled,
}) => (
    <div className={cn('flex flex-col', isLandscape && 'mb-3')}>
        <div
            className={cn(
                'flex',
                isPortrait ? 'flex-col items-center' : 'flex-row justify-center items-baseline gap-2'
            )}
        >
            <span className="font-bold text-center text-[24px] text-ultra-label tracking-[0.01em]">{label}</span>
            <div className={cn('flex items-baseline justify-center gap-[6px]', isPortrait && 'mb-[8px]')}>
                <span className={cn('font-bold text-white text-[30px] leading-[36px]')}>{amount}</span>
                {currency && <span className={cn('font-bold text-white text-[20px] leading-[24px]')}>{currency}</span>}
            </div>
        </div>
        <div
            className={cn(
                'flex items-center gap-3',
                isLandscape && 'mt-[5px]',
                disabled && 'pointer-events-none opacity-40'
            )}
        >
            <BetStepperButton
                type="minus"
                disabled={!canDecrement || disabled}
                onClick={onDecrement}
                className={cn('flex-shrink-0 w-[72px] h-[72px]')}
            />
            {/* Lift the slider (and its handle) above both steppers so the handle overlaps them the
                same way on each edge — always on top, never showing through. At the extremes the
                overlapped stepper is the disabled one, so this never blocks a usable button. */}
            <div className="relative z-10 flex-1 px-6">
                <Slider
                    min={0}
                    max={maxIndex}
                    value={currentIndex}
                    onChange={(val) => onIndexChange(Math.round(val))}
                    onSetEngineValue={(val) => onCommit?.(Math.round(val))}
                    showMinMaxValues={false}
                    handleClassName={'w-[64px] h-[64px]'}
                />
            </div>
            <BetStepperButton
                type="plus"
                disabled={!canIncrement || disabled}
                onClick={onIncrement}
                className={cn('flex-shrink-0 w-[72px] h-[72px]')}
            />
        </div>
    </div>
)

export default UltraPlaySliderRow
